/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace */

import { Injectable } from '@angular/core';
import {
  HttpClient, HttpHeaders, HttpParams
} from '@angular/common/http';

import {
  BehaviorSubject,
  Observable
} from 'rxjs';

import { map        } from 'rxjs/operators';

import { User       } from '@/models/user';

import { Logger     } from '@/util/logger';
import {PanoramaService} from "@/services/panorama.service";
import {AppRouteReuseStrategy} from "@/app.route.reuse.strategy";
import {RouteReuseStrategy} from "@angular/router";

@Injectable({
  providedIn: 'root'
})

/**
 *
 * @class AuthenticationService
 *
 * @brief injectable service that is also watchable; you can use login() and logout()
 * methods for authentication, but also watch the observableUser() to be notified to
 * changes of the currently logged in User.
 *
 * Angular tutorial including an authentication service:
 * @link https://jasonwatmore.com/post/2018/10/29/angular-7-user-registration-and-login-example-tutorial#app-component-html
 *
 * Observable data services:
 * @link https://blog.angular-university.io/how-to-build-angular2-apps-using-rxjs-observable-data-services-pitfalls-to-avoid/
 *
 * RxJS general primer:
 * @link https://www.learnrxjs.io/learn-rxjs/concepts/rxjs-primer
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      March 2020
 *
 */
export class AuthenticationService {

  private railviewCurrentUserSubject: BehaviorSubject<User>;
  private railviewCurrentUser: Observable<User>;

  /** standard constructor */
  constructor(private http: HttpClient,
              private routeReuseCache: RouteReuseStrategy) {

    this.railviewCurrentUserSubject = new BehaviorSubject<User>(JSON.parse(localStorage.getItem('railviewCurrentUser')));
    this.railviewCurrentUser        = this.railviewCurrentUserSubject.asObservable();
  }

  /** fetch the User of the session...but as an Observable */
  public get observableUser(): Observable<User> {

    return this.railviewCurrentUser;
  }

  /** fetch the User of the session...if there is one */
  public get userOfSession(): User {

    if (localStorage.getItem('railviewCurrentUser')) {

      const user = JSON.parse(localStorage.getItem('railviewCurrentUser'));

      return new User(user);
    }

    return this.railviewCurrentUserSubject.value;
  }

  /** fetch any headers we need to send along with our request */
  headers() : HttpHeaders {

    const user: User = this.userOfSession;

    if (!user) {

      /* no user logged in */

      return null;
    }

    return new HttpHeaders().set('Authorization', user.token);
  }

  /** check to see if the current user is an administrator or not */
  isAdmin(): boolean {

    const railviewCurrentUser: User = this.userOfSession;

    if (!railviewCurrentUser) {

      /* not logged in */

      return false;
    }

    if (!railviewCurrentUser.expiryAt) {

      /* user object is not set correctly (valid session has to have an expiry time) */

      return false;
    }

    const now: number = Math.floor(Date.now() / 1000);

    if (now >= railviewCurrentUser.expiryAt) {

      /* session expired so doesn't matter if they are or are not */

      return false;
    }

    return railviewCurrentUser.isAdmin;
  }

  /** try to extend a user's login session */
  public refresh() {

    const oldUser: User = this.userOfSession;

    if (!oldUser) {

      /* no user? */

      return ;
    }

    const headers = this.headers();

    if (!headers) {

      return null;
    }

    const params  = new HttpParams().

    set('userid', oldUser.username).
    set('token',  oldUser.refresh);

    Logger.debug('[Auth] refreshing access token for user...');

    return this.http.get<any>(`/backend/user/refresh`, {
      headers,
      params
    }).pipe(map(user => {

      if (user.result) {

        user = user.result;
      }

      /* login successful if there's a jwt token in the response */

      if (user && user.token) {

        /* update logged in user...*/

        localStorage.setItem('railviewCurrentUser', JSON.stringify(user));

        const loggedIn: User = new User(user);

        this.railviewCurrentUserSubject.next(loggedIn);

        Logger.debug('[Auth] re-logged in user: ', loggedIn, this.userOfSession);
      }

      return user;
    }));
  }

  /** try to login a user */
  public login(username: string, password: string) {

    return this.http.post<any>(`/backend/users/authenticate`, { username, password })
      .pipe(map(user => {

        if (user.result) {

          user = user.result;
        }

        /* login successful if there's a jwt token in the response */

        if (user && user.token) {

          /* store user details and jwt token in local storage to keep user logged in between page refreshes */

          localStorage.setItem('railviewCurrentUser', JSON.stringify(user));

          /* create a proper User model from the result, and save it, and notify any subscribers of the change */

          const loggedIn: User = new User(user);

          this.railviewCurrentUserSubject.next(loggedIn);

          Logger.debug('[Auth] logged in user: ', loggedIn, this.userOfSession);
        }

        return user;
      }));
  }

  /** try to login a user */
  public loginSSO(authCode: string) {

    return this.http.post<any>(`/backend/users/authenticate/sso?auth_code=${authCode}`, { auth_code: authCode })
      .pipe(map(user => {

        if (user.result) {

          user = user.result;
        }

        /* login successful if there's a jwt token in the response */

        if (user && user.token) {

          /* store user details and jwt token in local storage to keep user logged in between page refreshes */

          localStorage.setItem('railviewCurrentUser', JSON.stringify(user));

          /* create a proper User model from the result, and save it, and notify any subscribers of the change */

          const loggedIn: User = new User(user);

          this.railviewCurrentUserSubject.next(loggedIn);

          Logger.debug('[Auth] (SSO) logged in user: ', loggedIn, this.userOfSession);
        }

        return user;
      }));
  }

  /** logout the current user */
  public logout() {

    /* remove user from local storage to log user out */

    Logger.debug('[Auth] logging out...');

    localStorage.removeItem('railviewCurrentUser');

    this.railviewCurrentUserSubject.next(null);

    /* make sure the next user to login, can't just keep using this user's cached routes. */

    Logger.debug('[Auth] clearing the route reuse cache...');

    (this.routeReuseCache as AppRouteReuseStrategy).clearHandlers();

  }
}
